// Pure logic for the allowed-scopes ceiling editor — no React, no I/O. Kept
// separate from AllowedScopesEditor.tsx so the checkbox-state → payload and
// grouping rules are testable without rendering (Radix's checkbox/dialog
// primitives are awkward to drive in jsdom).
import { domainDisplayName, resourceDisplayName } from '@/features/roles'
import type { ProductStructure } from '@/features/roles'

export interface ScopeOption {
  id: string
  resourceLabel: string
  kind: 'read' | 'write'
}

export interface ScopeGroup {
  domain: string
  domainLabel: string
  options: ScopeOption[]
}

// Flattens one product's domain/resource spine into the delegation scopes the
// ceiling editor offers as checkboxes, grouped by domain. A resource
// contributes up to two independent options (its read scope and its write
// scope) — a read-only resource (no writeScope, e.g. orders.shipments)
// contributes only one.
export function scopeGroupsForProduct(structure: ProductStructure, language: string): ScopeGroup[] {
  return structure.domains.map((domain) => ({
    domain: domain.domain,
    domainLabel: domainDisplayName(domain, language),
    options: domain.resources.flatMap((resource) => {
      const resourceLabel = resourceDisplayName(resource, language)
      const options: ScopeOption[] = []
      if (resource.readScope) {
        options.push({ id: resource.readScope.id, resourceLabel, kind: 'read' })
      }
      if (resource.writeScope) {
        options.push({ id: resource.writeScope.id, resourceLabel, kind: 'write' })
      }
      return options
    }),
  }))
}

// Toggles one scope id in/out of the selected set, returning a new set (the
// component always replaces its state wholesale, never mutates in place).
export function toggleScope(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

// An empty selection IS the uncapped state (see AllowedScopesBase.kt) — this
// predicate is the single place that branch is decided, so the info banner,
// the empty-save confirmation, and any future caller all agree on it.
export function isUncapped(selected: ReadonlySet<string>): boolean {
  return selected.size === 0
}
